import { useCallback, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'
import type { GroupBase } from 'react-select'
import { useLazyQuery } from '@apollo/client'

import { Field } from 'Components/Field/Field'
import { FieldTypeEnum } from 'Components/Field/Field.types'
import type { SelectOption } from 'Components/Select/Select.types'
import { formatMultilineOptionLabel } from 'Components/SelectComponents/formatMultilineOptionLabel'

import type {
  getAvailablePhones,
  getAvailablePhonesVariables,
} from 'Constants/graphql/queries/listing/__generated__/getAvailablePhones'
import { GET_AVAILABLE_DEVICES } from 'Constants/graphql/queries/listing/GetDevices'

import type { ReplacementDeviceSelectProps } from './ReplacementDeviceSelect.types'

export const ReplacementDeviceSelect = <
  Option extends SelectOption,
  IsMulti extends boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: ReplacementDeviceSelectProps<Option, IsMulti, Group>,
) => {
  const { name, depotId, rentalId, ...rest } = props
  const [deviceOptions, setDeviceOptions] = useState<SelectOption[]>([])
  const { setValue } = useFormContext()

  const [getPhones, { loading: isLoading }] = useLazyQuery<
    getAvailablePhones,
    getAvailablePhonesVariables
  >(GET_AVAILABLE_DEVICES, {
    onCompleted: data => {
      const parsedDevices: SelectOption[] =
        data.rental.replacements?.stocklistItems?.map(stocklistItem => {
          const { deviceDefinition } = stocklistItem
          // IMEI and serial number go on their own lines,
          // see formatOptionLabel below
          const label =
            [
              deviceDefinition?.name,
              stocklistItem?.imei ? `IMEI ${stocklistItem.imei}` : null,
              stocklistItem?.serialNumber
                ? `SN ${stocklistItem.serialNumber}`
                : null,
            ]
              .filter((item): item is string => !!item)
              .join('\n') || ''

          return {
            label,
            value: stocklistItem.id.toString(),
          }
        }) ?? []

      // select first device on load
      if (parsedDevices.length > 0) {
        setValue(name, parsedDevices[0])
      }

      setDeviceOptions(parsedDevices)
    },
  })

  const defaultNoOptionsMessage = useCallback(
    () => <FormattedMessage id="No_devices_found" />,
    [],
  )

  useEffect(() => {
    if (!depotId || !rentalId) {
      return
    }

    getPhones({
      variables: {
        depotID: depotId,
        rentalID: rentalId,
      },
    })
  }, [depotId, getPhones, rentalId])

  return (
    <Field
      {...rest}
      name={name}
      selectProps={{
        formatOptionLabel: formatMultilineOptionLabel,
        isClearable: false,
        isLoading,
        noOptionsMessage: defaultNoOptionsMessage,
        options: deviceOptions,
      }}
      type={FieldTypeEnum.select}
    />
  )
}
